import React, { PropsWithChildren } from "react";
import { Portal } from "react-overlays";
import { useTransition, animated } from "react-spring";
import classNames from "classnames";

import { overlaysRef } from "src/App";
import useLayerEscapeClose from "src/hooks/useLayerEscapeClose";
import { closeIndependentDropdowns } from "src/utils/dropdownEvents";

import "./Pane.scss";

interface Props {
  showPane: boolean;
  hidePane: () => void;
  unsetOverflow?: boolean;
  width?: number;
  className?: string;
  backdropClassName?: string;
  disableBackdropClick?: boolean;
  disabledBackdropClickAction?: () => void;
}

function Pane(props: PropsWithChildren<Props>) {
  const {
    children,
    showPane,
    hidePane,
    unsetOverflow,
    width,
    className,
    backdropClassName,
    disableBackdropClick = false,
    disabledBackdropClickAction,
  } = props;

  React.useEffect(() => {
    if (showPane) {
      closeIndependentDropdowns();
    }
  }, [showPane]);

  const transitions = useTransition(showPane, {
    from: { x: 1200 },
    enter: { x: 0 },
    leave: { x: 1200 },
  });

  const backdropTransition = useTransition(showPane, {
    from: { opacity: 0 },
    enter: { opacity: 0.25 },
    leave: { opacity: 0 },
  });

  const handleBackdropClick = () => {
    if (disableBackdropClick) {
      disabledBackdropClickAction?.();
      return;
    }

    hidePane();
  };

  useLayerEscapeClose(hidePane, showPane);

  return (
    <Portal container={overlaysRef}>
      <>
        {backdropTransition(
          (style, item) =>
            item && (
              <animated.div
                style={style}
                className={classNames("pane-backdrop", backdropClassName)}
                onClick={handleBackdropClick}
              />
            ),
        )}
        {transitions(
          (styles, item) =>
            item && (
              <animated.div onClick={hidePane} className="pane-wrapper">
                <animated.div
                  onClick={(e) => e.stopPropagation()}
                  style={{ ...styles, width }}
                  className={classNames(
                    "pane",
                    {
                      "pane--default-position": !unsetOverflow,
                    },
                    className,
                  )}
                >
                  {children}
                </animated.div>
              </animated.div>
            ),
        )}
      </>
    </Portal>
  );
}

export { Pane };
